import { styled } from '@mui/system';
import { Box, Typography, Button, Divider } from '@mui/material';
import { fontFamilies, colors } from '../../constants/theme';
import { Link as NavLink } from 'react-router-dom';
import { figmaLetterSpacing } from '../../ui/utils';

export const FooterWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  paddingBottom: theme.spacing(3),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0, 3, 3),
  },
  [theme.breakpoints.up('md')]: {
    padding: '0 48px 0',
  },
}));

export const FooterContainer = styled(Box)({
  margin: '0 auto',
});

export const MainContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(4),
  marginBottom: theme.spacing(3),
  textAlign: 'center',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: '48px',
    marginBottom: '40px',
    textAlign: 'left',
  },
  [theme.breakpoints.up(1400)]: {
    gap: '98px',
    marginBottom: '48px',
  },
}));

export const LeftSection = styled(Box)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.up('md')]: {
    minWidth: 400,
    maxWidth: 494,
  },
  [theme.breakpoints.up('lg')]: {
    minWidth: 494,
  },
}));

export const LogoContainer = styled(Box)(({ theme }) => ({
  marginBottom: '24px',
  display: 'flex',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-start',
  },
}));

export const LogoImage = styled('img')(({ theme }) => ({
  height: 'auto',
  width: '100%',
  maxWidth: 494,
  cursor: 'pointer',
  [theme.breakpoints.down(1300)]: {
    maxWidth: 450,
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: 400,
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: 300,
  },
}));

export const Description = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies.quicksand,
  fontSize: '16px',
  lineHeight: '28px',
  color: colors.primary,
  [theme.breakpoints.up('md')]: {
    fontSize: '20px',
    lineHeight: '32px',
  },
}));

export const CenterSection = styled(Box)(({ theme }) => ({
  width: 'fit-content',
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '98px',
    marginTop: 0,
    flex: '0 0 auto',
  },
  [theme.breakpoints.down(1360)]: {
    gap: '48px',
  },
  [theme.breakpoints.down(1250)]: {
    flexDirection: 'column',
  },
}));

export const SitemapContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'flex-start',
}));

export const ButtonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  marginTop: 0,
  marginBottom: 'auto',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    marginLeft: 'auto',
  },
  [theme.breakpoints.down('md')]: {
    marginTop: '45px',
  },
}));

export const SitemapTitle = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies.gotham,
  fontSize: '20px',
  fontWeight: 400,
  color: '#999',
  letterSpacing: '1px',
  marginBottom: theme.spacing(2.5),
  textAlign: 'center',
  [theme.breakpoints.up('md')]: {
    textAlign: 'left',
  },
}));

export const SitemapGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto',
  rowGap: '30px',
  justifyItems: 'center',
  textAlign: 'center',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, minmax(auto, 1fr))',
    columnGap: '60px',
    justifyItems: 'center',
    textAlign: 'left',
  },
  [theme.breakpoints.up('md')]: {
    rowGap: theme.spacing(1.5),
    columnGap: '60px',
    justifyItems: 'start',
  },
  [theme.breakpoints.up(1310)]: {
    columnGap: '100px',
  },
}));

export const SitemapColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '30px',
}));

export const FooterLink = styled(NavLink)({
  fontFamily: fontFamilies.quicksand,
  fontSize: '16px',
  color: colors.primary,
  textDecoration: 'none',
  textWrap: 'nowrap',
  fontWeight: 400,
  lineHeight: '11px',
  letterSpacing: figmaLetterSpacing(16, 2),
  '&:hover': {
    color: '#492A18',
  },
});

export const CtaButton = styled(Button)(({ theme }) => ({
  color: colors.secondary,
  borderColor: colors.secondary,
  fontFamily: fontFamilies.quicksand,
  fontSize: '20px',
  fontWeight: 500,
  padding: theme.spacing(1.5, 3.75),
  textTransform: 'none',
  borderRadius: '8px',
  whiteSpace: 'nowrap',
  width: '100%',
  maxWidth: 360,
  height: '60px',
  [theme.breakpoints.up('sm')]: {
    width: 'auto',
    maxWidth: 'none',
  },
  '&:hover': {
    borderColor: '#492A18',
    backgroundColor: 'rgba(73, 42, 24, 0.04)',
  },
}));

export const FooterDivider = styled(Divider)(({ theme }) => ({
  border: '1px solid #39504C33',
}));

export const BottomSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  textAlign: 'center',
  margin: '32px 0',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(3),
    textAlign: 'left',
    margin: '44px 0',
  },
}));

export const Copyright = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies.hindGuntur,
  fontSize: '16px',
  fontWeight: 400,
  color: colors.primary,
  whiteSpace: 'pre-line',
  lineHeight: '28px',
  letterSpacing: '0%',
  [theme.breakpoints.up('md')]: {
    fontSize: '18px',
    lineHeight: '34px',
  },
}));

export const CopyrightBrand = styled('span')({
  fontWeight: 600,
  color: colors.primary,
});

export const LegalLinksContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '32px',
  flexWrap: 'wrap',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-start',
  },
}));

export const LegalLink = styled(NavLink)(({ theme }) => ({
  fontFamily: fontFamilies.quicksand,
  fontSize: '16px',
  fontWeight: 400,
  color: colors.primary,
  textDecoration: 'none',
  lineHeight: '100%',
  letterSpacing: '0%',
  [theme.breakpoints.up('md')]: {
    fontSize: '18px',
  },
  '&:hover': {
    color: colors.primary,
  },
}));

export const LegalLinksWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  alignSelf: 'flex-start',
  [theme.breakpoints.down('md')]: {
    alignSelf: 'center',
  },
}));

export const DisclaimerSection = styled(Box)(({ theme }) => ({
  maxWidth: 900,
  textAlign: 'center',
  paddingBottom: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    textAlign: 'left',
  },
}));

export const DisclaimerText = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies.hindGuntur,
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '20px',
  color: '#6B6B6B',
  '&:not(:last-child)': {
    marginBottom: theme.spacing(1.5),
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '14px',
    lineHeight: '22px',
  },
}));
